import React from 'react';
import { useTranslation } from 'next-i18next';
import { buildClassName } from 'utils/build-class-name';
import ScrollReveal, { StaggerReveal } from '../scroll-reveal';
import { LOCALE_HOME_PATHS, Namespaces } from 'types/locales';
import styles from './style.module.scss';

export interface TopMarket {
  name: string;
  slug: string;
  thumbnailUrl?: string;
  listingCount?: number;
}

interface TopMarketsProps {
  markets: TopMarket[];
  activeMarket?: string;
  onMarketClick?: (market: TopMarket) => void;
}

const AREA_THUMB_PATH = '/next/assets/images/local-focus/area-thumbnails';

/** Thumbnail URL for a market using area-thumbnails (city name + .jpg). Use when no explicit thumbnailUrl. */
export function getAreaThumbnailUrlForMarket(market: { name: string }): string {
  const cityPart = market.name.split(',')[0].trim();
  return `${AREA_THUMB_PATH}/${cityPart}.jpg`;
}

// Default popular US markets — thumbnails from area-thumbnails when file exists (e.g. Los Angeles.jpg)
export const DEFAULT_US_MARKETS: TopMarket[] = [
  { name: 'Los Angeles, CA', slug: 'los-angeles-ca', thumbnailUrl: `${AREA_THUMB_PATH}/Los Angeles.jpg` },
  { name: 'New York, NY', slug: 'new-york-ny', thumbnailUrl: `${AREA_THUMB_PATH}/New York.jpg` },
  { name: 'Chicago, IL', slug: 'chicago-il', thumbnailUrl: `${AREA_THUMB_PATH}/Chicago.jpg` },
  { name: 'Miami, FL', slug: 'miami-fl', thumbnailUrl: `${AREA_THUMB_PATH}/Miami.jpg` },
  { name: 'Austin, TX', slug: 'austin-tx', thumbnailUrl: `${AREA_THUMB_PATH}/Austin.jpg` },
  { name: 'San Francisco, CA', slug: 'san-francisco-ca', thumbnailUrl: `${AREA_THUMB_PATH}/San Francisco.jpg` },
];

// Default popular CA markets — thumbnails from area-thumbnails (jpgs)
export const DEFAULT_CA_MARKETS: TopMarket[] = [
  { name: 'Toronto, ON', slug: 'toronto-on', thumbnailUrl: `${AREA_THUMB_PATH}/Toronto.jpg` },
  { name: 'Vancouver, BC', slug: 'vancouver-bc', thumbnailUrl: `${AREA_THUMB_PATH}/Vancouver.jpg` },
  { name: 'Calgary, AB', slug: 'calgary-ab', thumbnailUrl: `${AREA_THUMB_PATH}/Calgary.jpg` },
  { name: 'Ottawa, ON', slug: 'ottawa-on', thumbnailUrl: `${AREA_THUMB_PATH}/Ottawa.jpg` },
  { name: 'Montreal, QC', slug: 'montreal-qc', thumbnailUrl: `${AREA_THUMB_PATH}/Montreal.jpg` },
  { name: 'Edmonton, AB', slug: 'edmonton-ab', thumbnailUrl: `${AREA_THUMB_PATH}/Edmonton.jpg` },
];

export default function TopMarkets({ markets, activeMarket, onMarketClick }: TopMarketsProps) {
  const { t } = useTranslation(Namespaces.HOME);
  return (
    <ScrollReveal className={styles['top-markets']}>
      <div className={styles.label}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_TOP_MARKETS_LABEL, { defaultValue: 'Explore Top Markets' })}</div>
      <StaggerReveal staggerDelay={0.06} className={styles['markets-row']}>
        {markets.map(market => {
          const thumbnailUrl = market.thumbnailUrl ?? getAreaThumbnailUrlForMarket(market);
          return (
            <button
              key={market.slug}
              type="button"
              className={buildClassName(
                styles['market-pill'],
                activeMarket === market.slug && styles.active
              )}
              onClick={() => onMarketClick?.(market)}
            >
              {/* Circular thumbnail placeholder */}
              <div className={styles['market-thumbnail']}>
                {thumbnailUrl ? (
                  <img src={thumbnailUrl} alt={market.name} loading="lazy" />
                ) : (
                  <div className={styles['thumbnail-placeholder']} />
                )}
              </div>
              <div className={styles['market-info']}>
                <span className={styles['market-name']}>{market.name}</span>
                {market.listingCount != null && (
                  <span className={styles['market-count']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_TOP_MARKETS_NEW_HOMES, { count: market.listingCount, defaultValue: '{{count}} New Homes' })}</span>
                )}
              </div>
            </button>
          );
        })}
      </StaggerReveal>
    </ScrollReveal>
  );
}
